import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, merge } from 'rxjs';

const PRODUCT_NAME = 'Tinello';

/** Route `title` is a translation key; the tab title follows the active language. */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private readonly _title = inject(Title);
  private readonly _transloco = inject(TranslocoService);
  private readonly _announcer = inject(LiveAnnouncer);
  private _key: string | undefined;
  private _navigated = false;
  private _announcePending = false;

  constructor() {
    super();
    // Never load translations here: the Router builds this during DI and a fetch re-enters AuthService.
    merge(
      this._transloco.langChanges$,
      this._transloco.events$.pipe(filter((event) => event.type === 'translationLoadSuccess')),
    ).subscribe(() => this._apply());
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const previousKey = this._key;
    this._key = this.buildTitle(snapshot);
    // Focus stays where it is on navigation, so a screen reader hears the new page only through this announcement; the first load is read by the browser itself,
    // and a query-only navigation keeps the title, so it stays silent.
    this._announcePending = this._navigated && this._key !== previousKey;
    this._navigated = true;
    this._apply();
  }

  private _apply(): void {
    // Until the language file has loaded, translate() would log a missing translation for a key that exists; the tab keeps its last title meanwhile.
    if (this._key && !this._isLoaded()) return;
    const text = this._key ? this._transloco.translate(this._key) : '';
    this._title.setTitle(text ? `${text} · ${PRODUCT_NAME}` : PRODUCT_NAME);
    if (this._announcePending && text) {
      this._announcePending = false;
      void this._announcer.announce(text);
    }
  }

  private _isLoaded(): boolean {
    return Object.keys(this._transloco.getTranslation(this._transloco.getActiveLang())).length > 0;
  }
}
